import { createReducer, on } from '@ngrx/store';
import {
  ActionDetailsDto,
  Comment,
  Procedure,
  ProcedureDetailsDto,
  ProcedureItemBase,
  ProcedureItemsResponse,
  ProcedureStatus,
} from 'projects/shared/src/lib/core/interfaces';
import {
  loadProcedures,
  loadProceduresFailure,
  loadProceduresSuccess,
  loadProcedureHeader,
  loadProcedureHeaderSuccess,
  loadProcedureHeaderFailure,
  loadProcedureItems,
  loadProcedureItemsSuccess,
  loadProcedureItemsFailure,
  refreshProcedureItems,
  fetchProcedureStatusesSuccess,
  deActivateProcedureSuccess,
  activateProcedureSuccess,
  toggleProcedureSidebar,
  fetchProcedureDetails,
  fetchProcedureDetailsSuccess,
  fetchProcedureDetailsFailure,
  hideProcedureSidebar,
  addProcedureCommentSuccess,
  setProcedureCommentPending,
  setProcedureCommentFailed,
  toggleActionItemSidebar,
  fetchActionDetails,
  fetchActionDetailsSuccess,
  fetchActionDetailsFailure,
  hideActionItemSidebar,
  addActionItemCommentSuccess,
  setActionItemCommentPending,
  setActionItemCommentFailed,
  selectProcedureItem,
  fetchProcedureItemStatusesSuccess,
  approveAllSubProcedures,
  approveAllSubProceduresSuccess,
  approveAllSubProceduresFailure,
  approveAllActions,
  approveAllActionsSuccess,
  approveAllActionsFailure,
  approvePolicyActions,
  approvePolicyActionsSuccess,
  approvePolicyActionsFailure,
} from './procedures.actions';

export const proceduresFeatureKey = 'procedures';

export interface ProcedureItemsState {
  procedureId: string | null;
  header: Procedure | null;
  items: ProcedureItemsResponse | null;
  selectedProcedureItem: ProcedureItemBase | null;
  isLoadingItems: boolean;
  isRefreshingItems: boolean;
  isUpdatingItem: boolean;
  allSubProceduresApproved: boolean;
  allActionsApproved: boolean;
  allPolicyActionsApproved: boolean;
  itemsApprovedCount: number;
  isApprovingAllSubProcedures: boolean;
  isApprovingAllActions: boolean;
  /** Policies with an in-flight "approve all actions" request, keyed by `policyId`. */
  approvingPolicyIds: Record<string, boolean>;
}

export interface ProcedureSidebarState {
  isOpen: boolean;
  procedureDetails: ProcedureDetailsDto | null;
  isLoading: boolean;
  commentPending: boolean;
  commentFailed: boolean;
  newComment?: Comment | null;
}

export interface ActionItemSidebarState {
  isOpen: boolean;
  actionDetails: ActionDetailsDto | null;
  isLoading: boolean;
  commentPending: boolean;
  commentFailed: boolean;
  newComment?: Comment;
}

export interface ProceduresState {
  procedures: Procedure[];
  openedProcedureId: string | null;
  isLoading: boolean;
  statuses?: ProcedureStatus[];
  itemStatuses?: ProcedureStatus[];
  procedureItems: ProcedureItemsState;
  sidebar: ProcedureSidebarState;
  actionItemSidebar: ActionItemSidebarState;
  procedureDetailsLoaded: boolean;
}

export const initialProcedureItemsState: ProcedureItemsState = {
  procedureId: null,
  header: null,
  items: null,
  selectedProcedureItem: null,
  isLoadingItems: false,
  isRefreshingItems: false,
  isUpdatingItem: false,
  allSubProceduresApproved: false,
  allActionsApproved: false,
  allPolicyActionsApproved: false,
  itemsApprovedCount: 0,
  isApprovingAllSubProcedures: false,
  isApprovingAllActions: false,
  approvingPolicyIds: {},
};

export const initialSidebarState: ProcedureSidebarState = {
  isOpen: false,
  procedureDetails: null,
  isLoading: false,
  commentPending: false,
  commentFailed: false,
  newComment: undefined,
};

export const initialActionItemSidebarState: ActionItemSidebarState = {
  isOpen: false,
  actionDetails: null,
  isLoading: false,
  commentPending: false,
  commentFailed: false,
  newComment: undefined,
};

export const initialState: ProceduresState = {
  procedures: [],
  openedProcedureId: null,
  isLoading: false,
  statuses: undefined,
  itemStatuses: undefined,
  procedureItems: initialProcedureItemsState,
  sidebar: initialSidebarState,
  actionItemSidebar: initialActionItemSidebarState,
  procedureDetailsLoaded: false,
};

const clearedApprovalPendingState = {
  isApprovingAllSubProcedures: false,
  isApprovingAllActions: false,
  approvingPolicyIds: {},
};

export const proceduresReducer = createReducer(
  initialState,
  on(loadProcedures, (state, { bypassLoader }) => ({
    ...state,
    isLoading: !bypassLoader,
  })),
  on(loadProceduresSuccess, (state, { procedures }) => ({
    ...state,
    procedures,
    isLoading: false,
  })),
  on(loadProceduresFailure, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(fetchProcedureStatusesSuccess, (state, { statuses }) => ({
    ...state,
    statuses,
  })),
  on(fetchProcedureItemStatusesSuccess, (state, { statuses }) => ({
    ...state,
    itemStatuses: statuses,
  })),
  // Procedure Items Reducers
  on(loadProcedureHeader, (state, { procedureId, bypassLoader }) => ({
    ...state,
    procedureItems: {
      ...state.procedureItems,
      procedureId,
      isLoadingHeader: !bypassLoader,
    },
  })),
  on(loadProcedureHeaderSuccess, (state, { procedure }) => ({
    ...state,
    procedureItems: {
      ...state.procedureItems,
      header: procedure,
      isLoadingHeader: false,
    },
  })),
  on(loadProcedureHeaderFailure, (state) => ({
    ...state,
    procedureItems: {
      ...state.procedureItems,
      isLoadingHeader: false,
    },
  })),
  on(loadProcedureItems, (state, { procedureId, bypassLoader }) => ({
    ...state,
    procedureItems: {
      ...state.procedureItems,
      procedureId,
      isLoadingItems: !bypassLoader,
    },
  })),
  // The quick-approval effects refresh the items on success, so the pending
  // flags are cleared once the refreshed list arrives rather than on the
  // approval response itself - otherwise the buttons would briefly re-enable
  // while still showing the stale statuses.
  on(loadProcedureItemsSuccess, (state, { items }) => ({
    ...state,
    procedureItems: {
      ...state.procedureItems,
      ...clearedApprovalPendingState,
      items,
      selectedProcedureItem: null,
      isLoadingItems: false,
      isRefreshingItems: false,
    },
  })),
  on(loadProcedureItemsFailure, (state) => ({
    ...state,
    procedureItems: {
      ...state.procedureItems,
      ...clearedApprovalPendingState,
      isLoadingItems: false,
      isRefreshingItems: false,
    },
  })),
  on(refreshProcedureItems, (state) => ({
    ...state,
    procedureItems: {
      ...state.procedureItems,
      isRefreshingItems: true,
    },
  })),

  on(
    activateProcedureSuccess,
    (state, { procedureId, newStatusCode, activatedOn }) => ({
      ...state,
      procedures: state.procedures.map((procedure) =>
        procedure.procedureId === procedureId
          ? {
              ...procedure,
              statusDisplayKey: newStatusCode,
              activatedOn,
              lastModified: activatedOn,
            }
          : procedure,
      ),
    }),
  ),
  on(deActivateProcedureSuccess, (state, { procedureId, newStatusCode }) => ({
    ...state,
    procedures: state.procedures.map((procedure) =>
      procedure.procedureId === procedureId
        ? {
            ...procedure,
            statusDisplayKey: newStatusCode,
            activatedOn: null,
          }
        : procedure,
    ),
  })),
  // Sidebar Reducers
  on(toggleProcedureSidebar, (state, { show, procedureId }) => {
    return {
      ...state,
      openedProcedureId: procedureId ?? null,
      procedureDetailsLoaded: false,
      sidebar: {
        ...state.sidebar,
        isOpen: show,
      },
    };
  }),
  on(hideProcedureSidebar, (state) => ({
    ...state,
    sidebar: initialSidebarState,
  })),
  on(fetchProcedureDetails, (state, { bypassLoader }) => ({
    ...state,
    procedureDetailsLoaded: !!bypassLoader,
    sidebar: {
      ...state.sidebar,
      isLoading: !bypassLoader,
    },
  })),
  on(fetchProcedureDetailsSuccess, (state, { procedureDetails }) => ({
    ...state,
    procedureDetailsLoaded: true,
    sidebar: {
      ...state.sidebar,
      procedureDetails,
      isLoading: false,
    },
  })),
  on(fetchProcedureDetailsFailure, (state) => ({
    ...state,
    sidebar: {
      ...state.sidebar,
      isLoading: false,
    },
  })),
  on(addProcedureCommentSuccess, (state, { comment }) => ({
    ...state,
    sidebar: {
      ...state.sidebar,
      procedureDetails: state.sidebar.procedureDetails
        ? {
            ...state.sidebar.procedureDetails,
            comments: [
              ...(state.sidebar.procedureDetails.comments || []),
              comment,
            ],
          }
        : null,
      commentPending: false,
      commentFailed: false,
      newComment: comment,
    },
  })),
  on(setProcedureCommentPending, (state, { pending }) => ({
    ...state,
    sidebar: {
      ...state.sidebar,
      commentPending: pending,
    },
  })),
  on(setProcedureCommentFailed, (state, { failed }) => ({
    ...state,
    sidebar: {
      ...state.sidebar,
      commentFailed: failed,
      commentPending: false,
    },
  })),
  // Action Item Sidebar Reducers
  on(toggleActionItemSidebar, (state, { show }) => ({
    ...state,
    actionItemSidebar: {
      ...state.actionItemSidebar,
      isOpen: show,
    },
  })),
  on(hideActionItemSidebar, (state) => ({
    ...state,
    actionItemSidebar: initialActionItemSidebarState,
    procedureItems: {
      ...state.procedureItems,
      selectedProcedureItem: null,
    },
  })),
  on(fetchActionDetails, (state, { bypassLoader }) => ({
    ...state,
    actionItemSidebar: {
      ...state.actionItemSidebar,
      isLoading: !bypassLoader,
    },
  })),
  on(fetchActionDetailsSuccess, (state, { actionDetails }) => ({
    ...state,
    actionItemSidebar: {
      ...state.actionItemSidebar,
      actionDetails,
      isLoading: false,
    },
  })),
  on(fetchActionDetailsFailure, (state) => ({
    ...state,
    actionItemSidebar: {
      ...state.actionItemSidebar,
      isLoading: false,
    },
  })),
  on(addActionItemCommentSuccess, (state, { comment }) => ({
    ...state,
    actionItemSidebar: {
      ...state.actionItemSidebar,
      actionDetails: state.actionItemSidebar.actionDetails
        ? {
            ...state.actionItemSidebar.actionDetails,
            comments: [
              ...(state.actionItemSidebar.actionDetails.comments || []),
              comment,
            ],
          }
        : null,
      commentPending: false,
      commentFailed: false,
      newComment: comment,
    },
  })),
  on(setActionItemCommentPending, (state, { pending }) => ({
    ...state,
    actionItemSidebar: {
      ...state.actionItemSidebar,
      commentPending: pending,
    },
  })),
  on(setActionItemCommentFailed, (state, { failed }) => ({
    ...state,
    actionItemSidebar: {
      ...state.actionItemSidebar,
      commentFailed: failed,
      commentPending: false,
    },
  })),
  on(selectProcedureItem, (state, { item }) => ({
    ...state,
    procedureItems: {
      ...state.procedureItems,
      selectedProcedureItem: item,
    },
  })),

  on(approveAllSubProcedures, (state) => ({
    ...state,
    procedureItems: {
      ...state.procedureItems,
      isApprovingAllSubProcedures: true,
    },
  })),
  on(approveAllSubProceduresFailure, (state) => ({
    ...state,
    procedureItems: {
      ...state.procedureItems,
      isApprovingAllSubProcedures: false,
    },
  })),
  on(approveAllActions, (state) => ({
    ...state,
    procedureItems: {
      ...state.procedureItems,
      isApprovingAllActions: true,
    },
  })),
  on(approveAllActionsFailure, (state) => ({
    ...state,
    procedureItems: {
      ...state.procedureItems,
      isApprovingAllActions: false,
    },
  })),
  on(approvePolicyActions, (state, { policyId }) => ({
    ...state,
    procedureItems: {
      ...state.procedureItems,
      approvingPolicyIds: {
        ...state.procedureItems.approvingPolicyIds,
        [policyId]: true,
      },
    },
  })),
  // The failure action carries no policyId, so every in-flight policy request
  // is released - there is at most one at a time in practice.
  on(approvePolicyActionsFailure, (state) => ({
    ...state,
    procedureItems: {
      ...state.procedureItems,
      approvingPolicyIds: {},
    },
  })),
  on(
    approveAllSubProceduresSuccess,
    (state, { allItemsApproved, itemsApprovedCount }) => ({
      ...state,
      procedureItems: {
        ...state.procedureItems,
        allSubProceduresApproved: allItemsApproved,
        itemsApprovedCount: itemsApprovedCount,
      },
    }),
  ),
  on(
    approveAllActionsSuccess,
    (state, { allItemsApproved, itemsApprovedCount }) => ({
      ...state,
      procedureItems: {
        ...state.procedureItems,
        allActionsApproved: allItemsApproved,
        itemsApprovedCount: itemsApprovedCount,
      },
    }),
  ),
  on(
    approvePolicyActionsSuccess,
    (state, { allItemsApproved, itemsApprovedCount }) => ({
      ...state,
      procedureItems: {
        ...state.procedureItems,
        allPolicyActionsApproved: allItemsApproved,
        itemsApprovedCount: itemsApprovedCount,
      },
    }),
  ),
);
